import { yup } from '@framework/validation';
import {
  IParentWidgetScope,
  IWidgetType,
} from '../../schema/widgets/IWidgetType';
import { Widget } from '../../schema/widgets/Widget';
import { setSchemaRules } from './formBuilderSchemaRules';
import {
  CURRENT_PAGE_STEP,
  CURRENT_SECTION_STEP,
  VALIDATION_TYPE,
  ValidationType,
} from './types/FormBuilderTypes';

/**
 * Imposta una regola per evitare di validare step (sezioni/pagine) ancora non visualizzate.
 *
 * Per farlo imposta un meta specifico (`applyRules`) che viene poi utilizzato in `setSchemaRules`
 * (nella setValidationRules) per decidere se applicare o meno le regole di validazione.
 */
export function setStepValidation<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W> | null,
  params: {
    widgetSectionStep: number;
    widgetPageStep: number;
    parentScope: IParentWidgetScope;
  },
) {
  const { widgetSectionStep, widgetPageStep, parentScope } = params;

  const newSchema = schema
    .meta({ step: widgetSectionStep, page: widgetPageStep })
    .when(
      [
        `$${CURRENT_SECTION_STEP}`,
        `$${CURRENT_PAGE_STEP}`,
        `$${VALIDATION_TYPE}`,
      ],
      (
        [currentSectionStep, currentPageStep, validationType]: any[],
        schema: any,
      ) => {
        // Gli step partono da 1 perché la 0 è la Guida alla Compilazione
        const currentValidationStep = currentSectionStep - 1;

        let applyRules = true;

        // SE viene richiesta la validazione completa, allora imposto tutte le regole su tutti i campi
        if (validationType === ValidationType.COMPLETE) {
          applyRules = true;
        }

        // Se non è richiesta la validazione completa,
        // allora imposto le regole solo ai widget nella
        // stessa sezione dalla pagina 1 fino a quella corrente
        // Altrimenti, se la pagina è null, assumo la validazione parziale
        // per tutta la sezione
        if (validationType === ValidationType.PARTIAL) {
          applyRules =
            widgetSectionStep === currentValidationStep &&
            (currentPageStep == null || widgetPageStep <= currentPageStep);
        }

        // Se è richiesta la validazione parziale per il salvataggio in bozza,
        // allora non applico le regole del widget, ma solo visibilità/abilitazione/etc
        if (
          validationType === ValidationType.DRAFT ||
          validationType === ValidationType.TESTING
        ) {
          applyRules = false;
        }

        return schema.meta({ applyRules });
      },
    );

  // Applica le regole allo schema (N.B. le regole di validazione vengono applicate solo se il meta "applyRules" è true)
  return setSchemaRules(newSchema, {
    widget,
    widgetType,
    parentScope,
  });
}
